import { useCallback, useEffect, useState } from "react";
import useWebotServer from "./useWebotServer";
import useAuth from "./useAuth";
import { CoinBalanceBreakdown } from "../@types/sharedSchema";

/**
 * Fetches the authenticated user's coin balance from the backend
 * (GET /api/v1/coins/balance). Call `refetch` after any operation that spends or
 * adds coins (e.g. generating a new creative) to keep the displayed balance fresh.
 */
export default function useCoinBalance() {
  const { user } = useAuth();
  const { getMyCoinBalance } = useWebotServer();
  const [balance, setBalance] = useState<CoinBalanceBreakdown | null>(null);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    if (!user?.id) {
      setBalance(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const fetched = await getMyCoinBalance();
      setBalance(fetched);
    } catch (error) {
      console.error("useCoinBalance: failed to fetch balance", error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { balance, loading, refetch };
}
